import React from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { PlayerState } from '../playerState';
import { ControlButton } from './controlButton';
import { UserPlayerFullscreenSVGIcon, SquarePlusSVGIcon, CheckmarkSVGIcon, CloseSVGIcon, BodyCamSvgIcon, DocBodyCamSvgIcon, TranslatedDocumentSVGIcon, DualPlusReportSVGIcon, CustomerFollowUpLetterSvgIcon } from '../../svg/svg';
import { EmbedButton, PlayPauseButton, SeekBackButton, SeekNextButton } from './controlButtons';
import { VolumeButton } from './volumeButton';
import { PinButton } from './pinButton';
import { SpeedButton } from './speedButton';
import { durationToString } from '../../../core/time';
import { ReactionButton } from './reactionButton';
import { CaptionsButton } from './captionsButton';
import { PlayerComponentName } from '../playerSchema';
import { AudioTrackButton } from './audioTrackButton';

type Props = {
  model: PlayerState,
  ignoreChromeVisibilityForReactions?: boolean
}

type RelatedPublicSafetyJob = NonNullable<NonNullable<PlayerState['job']>['relatedPublicSafetyJobs']>[number];

const getRelatedJobOfficerLabel = (job: RelatedPublicSafetyJob) =>
  [job.publicSafetyOfficerTitle, job.publicSafetyOfficerName].filter(Boolean).join(' ')
  || job.publicSafetyOfficerName
  || job.user?.name
  || job.user?.email
  || job.title
  || job.id;

const hasRelatedJobId = (job: RelatedPublicSafetyJob): job is RelatedPublicSafetyJob & { id: string } =>
  typeof job.id === 'string' && job.id.length > 0;

export const PlayerControlsBar = observer(({
  model,
  ignoreChromeVisibilityForReactions = false
}: Props) => {

  const indexIsVisible = model.frameset?.isSectionVisible('Index');
  const commentsIsVisible = model.frameset?.isSectionVisible('Comments');
  const editor = model.momentEditor;

  const stub = editor.currentStub;
  const showAddMomentBtn = model.isComponentVisible(PlayerComponentName.AddMomentButton);

  const showReactionsButton = model.showReactionsButton;
  const allowFullscreen = model.allowFullscreen ?? true;

  const qs = new URLSearchParams(window.location.search);
  const teamId = qs.get('teamId') || undefined;
  const selectionRangeClassName = classNames('stub-time-range', {
    'small': model.isWidgetMode && (indexIsVisible || commentsIsVisible) && !teamId
  });

  const showPlaybackRateButton = model.isComponentVisible(PlayerComponentName.PlaybackRateButton);
  const showAudioTrackButton = model.isComponentVisible(PlayerComponentName.AudioTrackButton);
  const showClosedCaptionsButton = model.isComponentVisible(PlayerComponentName.ClosedCaptionsButton);
  const showVolumeButton = model.isComponentVisible(PlayerComponentName.VolumeButton);
  const isManualReportOnly = model.isManualReportOnly;
  const showPublicSafetyNotes = !isManualReportOnly && !!model.job?.publicSafetyNotes;
  const showPublicSafetyReport = !isManualReportOnly && !!model.job?.publicSafetyReport;
  const showCustomerFollowUpLetter = !!model.job?.publicSafetyCustomerFollowUpLetter;
  const showTranslation = !!model.job?.isDocumentTranslation && !!model.job?.isDone;
  const relatedPublicSafetyJobs = model.job?.relatedPublicSafetyJobs ?? [];
  const showDualPlusButton = !!model.job?.hasDualPlusPublicSafetyJobs;
  const dualPlusTooltip = (
    <div className="dual-plus-tooltip">
      <strong>{model.job?.speciality === 'Custom' ? 'DUI case review' : 'Dual+ case'}{model.job?.publicSafetyCaseId ? ` ${model.job.publicSafetyCaseId}` : ''}</strong>
      {relatedPublicSafetyJobs.map(job => (
        <span key={job.id}>{getRelatedJobOfficerLabel(job)}</span>
      ))}
    </div>
  );

  return (
    <div id="user-player-controls-bar">

      <div className="left-controls">
        <div className="control-group">
          {!model.isLiveStream
            && <PlayerStaticControls model={model} />}

          {model.isLiveStream
            && <PlayerLiveControls model={model} />}
        </div>

        {showReactionsButton && (
          <div className="control-group">
            <ReactionButton
              model={model}
              ignoreChromeVisibility={ignoreChromeVisibilityForReactions} />
          </div>
        )}

        {(showPublicSafetyNotes || showPublicSafetyReport || showCustomerFollowUpLetter) && (
          <div className="control-group"> 
            {showPublicSafetyNotes && <ControlButton
              model={model}
              className="player-body-cam-button"
              tooltip="First Draft Notes"
              icon={<BodyCamSvgIcon />}
              data-guided-training-id="player-training-notes"
              onClick={() => model.openEditorWindow(`${model.job?.title}-notes.pdf`, 'notes' )} />}

            {showPublicSafetyReport && <ControlButton
              model={model}
              className="player-body-cam-button"
              tooltip="First Draft Report"
              icon={<DocBodyCamSvgIcon />}
              data-guided-training-id="player-training-report"
              onClick={() => model.openEditorWindow(`${model.job?.title}-reports.pdf`, 'report' )} />}

            {showCustomerFollowUpLetter && <ControlButton
              model={model}
              className="player-body-cam-button"
              tooltip="Customer Follow-Up Letter"
              icon={<CustomerFollowUpLetterSvgIcon />}
              onClick={() => model.openEditorWindow(`${model.job?.title}-follow-up-letter.pdf`, 'customerFollowUpLetter')} />}
          </div>
        )}

        {showDualPlusButton && (
          <div className="control-group dual-plus-controls">
            <ControlButton
              className="dual-plus-control-button"
              icon={<DualPlusReportSVGIcon />}
              tooltip={dualPlusTooltip}
              model={model}
              onClick={() => void model.openDualPlusPublicSafetyReport()} />

            {relatedPublicSafetyJobs.filter(hasRelatedJobId).map(relatedJob => {
              const officerLabel = getRelatedJobOfficerLabel(relatedJob);
              const notesAvailable = !!relatedJob.publicSafetyNotes;
              const reportAvailable = !!relatedJob.publicSafetyReport;

              return (
                <div className="dual-plus-related-job-controls" key={relatedJob.id}>
                  <ControlButton
                    className="dual-plus-related-job-button"
                    icon={<BodyCamSvgIcon />}
                    tooltip={`${officerLabel} notes${notesAvailable ? '' : ' not available'}`}
                    model={model}
                    disabled={!notesAvailable}
                    onClick={() => void model.openPublicSafetyJobEditorWindow(
                      relatedJob.id,
                      `${relatedJob.title || officerLabel}-notes.pdf`,
                      'notes'
                    )} />

                  <ControlButton
                    className="dual-plus-related-job-button"
                    icon={<DocBodyCamSvgIcon />}
                    tooltip={`${officerLabel} report${reportAvailable ? '' : ' not available'}`}
                    model={model}
                    disabled={!reportAvailable}
                    onClick={() => void model.openPublicSafetyJobEditorWindow(
                      relatedJob.id,
                      `${relatedJob.title || officerLabel}-reports.pdf`,
                      'report'
                    )} />
                </div>
              );
            })}
          </div>
        )}

        {isManualReportOnly && (
          <div className="control-group">
            <ControlButton
              model={model}
              className="player-body-cam-button"
              tooltip="First Draft Report"
              icon={<DocBodyCamSvgIcon />}
              data-guided-training-id="player-training-report"
              onClick={() => model.openEditorWindow(`${model.job?.title}-reports.pdf`, 'report')} />
          </div>
        )}

        {showTranslation && (
          <div className="control-group">
            <ControlButton
              model={model}
              className="player-body-cam-button"
              tooltip="Translated Document"
              icon={<TranslatedDocumentSVGIcon />}
              onClick={() => model.openEditorWindow(`${model.job?.title}-translation`, 'translation')} />
          </div>
        )}

        {showAddMomentBtn && (
          <div className="control-group">
            {model.isEditMode && (
              <>
                <ControlButton
                  className="exit-edit-mode-btn"
                  icon={<CloseSVGIcon />}
                  tooltip="Cancel"
                  model={model}
                  onClick={_evt => editor.exitEditMode()} />

                <ControlButton
                  className="edit-confirm-moment-btn"
                  icon={<CheckmarkSVGIcon />}
                  tooltip="Save selection"
                  model={model}
                  onClick={_evt => editor.editConfirmMoment(model.openMomentWindowOptions)} />
              </>
            )}

            {!model.isEditMode && (
              <ControlButton
                id="add-clip-btn-widget"
                data-tutorial-id="add-clip-btn-widget"
                icon={<SquarePlusSVGIcon />}
                tooltip="Add a moment"
                model={model}
                onClick={_evt => editor.enterEditMode()} />
            )}
          </div>)}

        {model.isEditMode &&
          <div className="control-group">

            <div className="control-text flex-center gap2">
              <span>Your Selection:</span>
              <strong className={selectionRangeClassName}>
                <span className="stub-start-time">
                  {durationToString(stub?.startTime, 'time')}
                </span>
                <span> - </span>
                <span className="stub-end-time">
                  {durationToString(stub?.endTime, 'time')}
                </span>
              </strong>
            </div>
          </div>}
      </div>

      <div className="right-controls">

        <div className="control-group">
          {showAudioTrackButton &&
            <AudioTrackButton
              model={model} />}

          {showPlaybackRateButton &&
            <SpeedButton
              model={model} />}

          <EmbedButton
            model={model} />

          <PinButton
            model={model} />

          {showVolumeButton &&
            <VolumeButton
              model={model} />}

          {showClosedCaptionsButton &&
            <CaptionsButton
              model={model} />}

          {allowFullscreen &&
            <ControlButton model={model}
              className={`fullscreen-btn`}
              tooltip={model.isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              icon={<UserPlayerFullscreenSVGIcon />}
              onClick={_evt => model.invoke('toggleFullscreen')} />}
        </div>
      </div>
    </div>
  );
});

export const PlayerLiveControls = observer(({
  model
}: Props) => {
  const timeLabelClassName = model.duration > 3600 ? 'time-label long' : 'time-label short';

  // TODO: unify with PlayerStaticControls via getComponentPolicy once ads are supported on live streams

  return <>
    {model.isSeekable &&
      <SeekBackButton
        model={model} />}

    <PlayPauseButton
      model={model} />

    {model.isSeekable &&
      <SeekNextButton
        model={model} />}

    {model.isLiveStreamRunning &&
      <div className={`control-live 
    ${model.isRealTime ? 'active' : ''}`}
        onClick={() => model.invoke('toggleRealTimeMode')}>
        LIVE
      </div>}

    {(model.isLiveStream && model.isSeekable) &&
      <div className="control-time-live">
        <span className={"current " + timeLabelClassName}>
          {model.liveOffsetLabel}
        </span>
      </div>}
  </>
})

export const PlayerStaticControls = observer(({
  model
}: Props) => {
  const timeLabelClassName = model.duration > 3600 ? 'time-label long' : 'time-label short';

  const showPlayPauseBtn = model.isComponentVisible(PlayerComponentName.PlayPauseButton)
  const showSeekBackBtn = model.isComponentVisible(PlayerComponentName.SeekBackButton);
  const showSeekNextBtn = model.isComponentVisible(PlayerComponentName.SeekNextButton);

  return <>
    {showSeekBackBtn &&
      <SeekBackButton
        model={model} />}

    {showPlayPauseBtn &&
      <PlayPauseButton
        model={model} />}

    {showSeekNextBtn &&
      <SeekNextButton
        model={model} />}

    <div className="control-time">
      <span className={"current " + timeLabelClassName}>
        {model.currentTimeLabel}
      </span>
      <span className="separator">
        /
      </span>
      <span className={"duration " + timeLabelClassName}>
        {model.durationLabel}
      </span>
    </div>
  </>
});
